import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { jobAPI } from '../../services/api';
import './JobPostingCTA.css';

const JobPostingCTA = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [postedJobs, setPostedJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchPostedJobs();
    }
  }, [user]);

  const fetchPostedJobs = async () => {
    if (!user) {
      console.log('JobPostingCTA: No user found');
      return;
    }
    
    setLoading(true);
    try {
      console.log('JobPostingCTA: Fetching jobs for user:', user.id);
      // Use jobAPI.getAll with posted_by filter to get only user's own jobs
      // When fetching user's own jobs, we want to see all statuses (not just published/active)
      const params = {
        posted_by: user.acting_user_id ?? user.id,
        per_page: 5
      };
      
      console.log('JobPostingCTA: Request params:', params);
      const response = await jobAPI.getAll(params);
      
      console.log('JobPostingCTA: API Response:', response);
      console.log('JobPostingCTA: Full URL would be:', `/jobs?posted_by=${user.id}&per_page=5`);
      
      if (response.success) {
        // Handle paginated response
        const jobs = response.data?.data || response.data?.items || response.data || [];
        console.log('JobPostingCTA: Jobs found:', jobs.length, jobs);
        setPostedJobs(jobs);
      } else {
        console.warn('JobPostingCTA: API returned success=false:', response);
        setPostedJobs([]);
      }
    } catch (err) {
      console.error('JobPostingCTA: Error fetching posted jobs:', err);
      setPostedJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMyJobsClick = () => {
    navigate('/my-jobs');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  // Helper function to strip HTML tags for preview
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const formatSalary = (job) => {
    if (!job.salary_min && !job.salary_max) {
      return null;
    }
    
    const currency = job.salary_currency || 'USD';
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : currency;
    const period = job.salary_period === 'yearly' ? 'year' : 
                   job.salary_period === 'monthly' ? 'month' :
                   job.salary_period === 'hourly' ? 'hour' : job.salary_period;
    
    if (job.salary_min && job.salary_max) {
      return `${symbol}${job.salary_min.toLocaleString()} - ${symbol}${job.salary_max.toLocaleString()}/${period}`;
    } else if (job.salary_min) {
      return `${symbol}${job.salary_min.toLocaleString()}+/${period}`;
    }
    return null;
  };

  const handleJobClick = (job) => {
    navigate(`/my-jobs?jobId=${job.id}`);
  };

  return (
    <div className="personal-jobs-section">
      <div className="section-header">
        <h3>Jobs</h3>
        <button 
          className="header-btn"
          onClick={handleMyJobsClick}
          title="My Jobs"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
          </svg>
          <span>My Jobs</span>
        </button>
      </div>

      {loading ? (
        <div className="jobs-loading">
          <div className="jobs-spinner"></div>
          <span>Loading jobs...</span>
        </div>
      ) : postedJobs.length > 0 ? (
        <div className="posted-jobs-list">
          {postedJobs.map((job) => (
            <div 
              key={job.id} 
              className="posted-job-card"
              onClick={() => handleJobClick(job)}
            >
              <div className="posted-job-header">
                <div className="posted-job-title-section">
                  <h4 className="posted-job-title">{job.title}</h4>
                  <div className="posted-job-meta">
                    <span className="posted-job-company">
                      {job.company?.name || 'Your Company'}
                    </span>
                    {job.work_type && (
                      <>
                        <span className="posted-job-separator">•</span>
                        <span className="posted-job-type">{job.work_type}</span>
                      </>
                    )}
                    {job.job_type && (
                      <>
                        <span className="posted-job-separator">•</span>
                        <span className="posted-job-type">{job.job_type}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="posted-job-status">
                  {job.is_active && job.status === 'published' ? (
                    <span className="status-badge active">Active</span>
                  ) : (
                    <span className="status-badge inactive">Inactive</span>
                  )}
                </div>
              </div>
              
              {job.description && (
                <p className="posted-job-description">
                  {(() => {
                    const plainText = stripHtml(job.description);
                    return plainText.length > 120 
                      ? `${plainText.substring(0, 120)}...` 
                      : plainText;
                  })()}
                </p>
              )}

              <div className="posted-job-footer">
                <div className="posted-job-stats">
                  {job.application_count > 0 && (
                    <span className="posted-job-applications">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      {job.application_count} {job.application_count === 1 ? 'application' : 'applications'}
                    </span>
                  )}
                  {formatSalary(job) && (
                    <span className="posted-job-salary">{formatSalary(job)}</span>
                  )}
                </div>
                <span className="posted-job-date">{formatDate(job.created_at)}</span>
              </div>
            </div>
          ))}
          
          {postedJobs.length > 0 && (
            <button 
              className="view-all-jobs-btn"
              onClick={() => navigate('/my-jobs')}
            >
              View all jobs
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12,5 19,12 12,19"></polyline>
              </svg>
            </button>
          )}
        </div>
      ) : (
        <div className="no-jobs-state">
          <div className="no-jobs-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
          </div>
          <div className="no-jobs-content">
            <h4>No Jobs Posted Yet</h4>
            <p>Go to My Jobs to post your first job opening and start building your team today.</p>
            <button 
              className="go-to-my-jobs-btn"
              onClick={handleMyJobsClick}
            >
              Go to My Jobs
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12,5 19,12 12,19"></polyline>
              </svg>
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default JobPostingCTA;

